import React from "react";
import { Link } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { brandUrl } from "@/lib/brands";
import { Recycle, ArrowUpRight, MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

const Footer = () => {
  const { brand, sister } = useBrand();

  return (
    <footer
      className="relative bg-[color:var(--bg-dark)] text-white overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 grid-lines-dark opacity-70 pointer-events-none" />
      <div className="relative container-x pt-24 pb-10">
        {/* Sister Yard Cross-Link — hero banner */}
        <div
          className="border-2 border-white/20 p-8 md:p-12 mb-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6 hover:border-[color:var(--accent)] transition-colors"
          data-testid="sister-yard-banner"
        >
          <div>
            <div className="eyebrow text-[color:var(--accent)] mb-3">
              Our Other Yard
            </div>
            <h3 className="font-display text-3xl md:text-5xl uppercase leading-none">
              {sister.name}
            </h3>
            <p className="font-mono text-xs uppercase tracking-widest text-white/60 mt-3">
              {sister.city}, {sister.state} · {sister.address}
            </p>
          </div>
          <a
            href={brandUrl(sister.slug)}
            className="hard-btn-inverse group self-start md:self-auto"
            data-testid="visit-sister-yard-btn"
          >
            Visit {sister.short_name}
            <ArrowUpRight
              size={14}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white text-black grid place-items-center">
                <Recycle size={18} strokeWidth={2.5} />
              </div>
              <div className="font-display uppercase">{brand.short_name}</div>
            </div>
            <p className="text-white/70 max-w-md text-sm leading-relaxed">
              A 100% minority women-owned full metal recycling facility serving
              the southern region with {brand.established.toLowerCase()} of
              industry experience. Two yards. One mission: conserve resources
              through responsible recycling.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.facebook.com/profile.php?id=100064748658800"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 border border-white/30 grid place-items-center hover:bg-white hover:text-black transition-colors"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook size={14} />
              </a>
              <a
                href="https://www.instagram.com/dalesrecycling_queencitymetal/"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 border border-white/30 grid place-items-center hover:bg-white hover:text-black transition-colors"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram size={14} />
              </a>
              <a
                href="https://www.linkedin.com/in/mickey-coffino-6024b9a/"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 border border-white/30 grid place-items-center hover:bg-white hover:text-black transition-colors"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
              >
                <Linkedin size={14} />
              </a>
              <a
                href="https://www.youtube.com/@mickeycoffino"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 border border-white/30 grid place-items-center hover:bg-white hover:text-black transition-colors"
                aria-label="YouTube"
                data-testid="social-youtube"
              >
                <Youtube size={14} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-white/50 mb-4">Visit</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex gap-2">
                <MapPin size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
                <a href={brand.map_url} target="_blank" rel="noreferrer">
                  {brand.address}
                </a>
              </li>
              <li className="flex gap-2">
                <Phone size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
                <a href={`tel:${brand.phone.replace(/[^0-9]/g, "")}`}>{brand.phone}</a>
              </li>
              <li className="flex gap-2">
                <Mail size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-white/50 mb-4">Explore</div>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li><a href="#materials" className="hover:text-white">Materials</a></li>
              <li><a href="#services" className="hover:text-white">Services</a></li>
              <li><a href="#about" className="hover:text-white">About</a></li>
              <li><a href="#prison-reform" className="hover:text-white">Second Chance</a></li>
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-white/50 mb-4">Yards</div>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li>
                <a href={brandUrl("qcm")} className="hover:text-white">Charlotte · QCM</a>
              </li>
              <li>
                <a href={brandUrl("dales")} className="hover:text-white">Hudson · Dale's</a>
              </li>
              <li>
                <Link to="/admin" className="hover:text-white">Admin</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-xs text-white/40 font-mono uppercase tracking-widest">
            © {new Date().getFullYear()} {brand.name}. All Rights Reserved.
          </div>
          <div className="text-xs text-white/40 font-mono uppercase tracking-widest">
            {brand.website} · Sister Yard: {sister.website}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
